import { useState, useEffect, createContext, useContext } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { UserRole } from "@/types/database";

interface AuthUser {
  id: string;
  role: UserRole;
  name: string;
  email?: string | null;
  class_id?: number | null;
  coins: number;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signInWithPin: (pin: string, studentId?: string) => Promise<AuthUser>;
  signInWithEmail: (email: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function useAuthProvider() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        await loadUserProfile(session.user);
      }

      setLoading(false);
    };

    checkUser();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        await loadUserProfile(session.user);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (authUser: User) => {
    try {
      // For students using PIN auth, we'll have stored their profile in metadata
      if (authUser.user_metadata?.role === "student") {
        setUser(authUser.user_metadata as AuthUser);
        return;
      }

      // For staff, load from users table
      if (!authUser.email) {
        throw new Error("No email found for user");
      }
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("email", authUser.email)
        .single();

      if (error) throw error;

      setUser({
        id: data.id,
        role: data.role,
        name: data.name,
        email: data.email,
        class_id: null, // users table is for staff, no class_id
        coins: data.coins ?? 0,
      });
    } catch (error) {
      console.error("Error loading user profile:", error);
      setUser(null);
    }
  };

  const signInWithPin = async (
    pin: string,
    studentId?: string,
  ): Promise<AuthUser> => {
    try {
      // Hash the PIN to match database
      const pinHash = btoa(pin); // Simple encoding for demo - use proper hashing in production

      // Build query - if studentId provided, use it to disambiguate
      // Students are now in the children table
      let query = supabase
        .from("children")
        .select("*")
        .eq("pin_hash", pinHash)
        .eq("role", "student");

      if (studentId) {
        query = query.eq("id", studentId);
      }

      // Find user by PIN hash (and optional student ID)
      const { data: userData, error: userError } = await query.single();

      if (userError || !userData) {
        throw new Error("Invalid PIN. Please check your 4-digit PIN.");
      }

      // Create a temporary session for the student
      // Combine first_name and last_name for display name
      const studentUser: AuthUser = {
        id: userData.id,
        role: (userData.role ?? "student") as UserRole,
        name: `${userData.first_name} ${userData.last_name}`,
        email: userData.email,
        class_id: userData.class_id,
        coins: userData.coins ?? 0,
      };

      setUser(studentUser);

      return studentUser;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : "Login failed");
    }
  };

  const signInWithEmail = async (
    email: string,
    password: string,
  ): Promise<AuthUser> => {
    try {
      // DEBUG: Step 1 - Auth
      console.log("🔐 Step 1: Authenticating with Supabase Auth...");
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("❌ Step 1 FAILED: Auth error", error);
        throw error;
      }

      if (!data.user) {
        console.error("❌ Step 1 FAILED: No user returned from auth");
        throw new Error("Login failed");
      }

      console.log("✅ Step 1 SUCCESS: Auth successful. User ID:", data.user.id);

      // DEBUG: Step 2 - Load profile
      console.log("📋 Step 2: Loading user profile from database...");
      console.log("   Email being queried:", data.user.email);

      if (!data.user.email) {
        throw new Error("No email found for authenticated user");
      }
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("*")
        .eq("email", data.user.email)
        .single();

      if (userError) {
        console.error("❌ Step 2 FAILED: Database error", userError);
        console.error("   Error code:", userError.code);
        console.error("   Error details:", JSON.stringify(userError, null, 2));
        throw userError;
      }

      if (!userData) {
        console.error("❌ Step 2 FAILED: No user data returned");
        throw new Error("User profile not found");
      }

      console.log("✅ Step 2 SUCCESS: User data loaded. Role:", userData.role);

      // DEBUG: Step 3 - Create session
      console.log("👤 Step 3: Creating auth session...");
      const authUser: AuthUser = {
        id: userData.id,
        role: userData.role,
        name: userData.name,
        email: userData.email,
        class_id: null, // users table is for staff, no class_id
        coins: userData.coins ?? 0,
      };

      console.log("✅ Step 3 SUCCESS: Auth user created:", {
        id: authUser.id,
        name: authUser.name,
        role: authUser.role,
        coins: authUser.coins,
      });

      // Update state (auth listener will also update it)
      console.log("🔄 Step 4: Updating React state...");
      setUser(authUser);
      console.log("✅ Step 4 SUCCESS: State updated");

      return authUser;
    } catch (error) {
      console.error("💥 LOGIN FAILED:", error);
      throw new Error(error instanceof Error ? error.message : "Login failed");
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      // Sign out from Supabase auth
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);

      // Note: Components should handle navigation after calling signOut()
      // This avoids full page reload and uses React Router navigation
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  const refreshUser = async () => {
    if (!user) return;

    try {
      // Students are in children table, staff are in users table
      if (user.role === "student") {
        const { data, error } = await supabase
          .from("children")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) throw error;

        setUser({
          id: data.id,
          role: (data.role ?? "student") as UserRole,
          name: `${data.first_name} ${data.last_name}`,
          email: data.email,
          class_id: data.class_id,
          coins: data.coins ?? 0,
        });
      } else {
        // Staff (teachers, librarians)
        const { data, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) throw error;

        setUser({
          id: data.id,
          role: data.role,
          name: data.name,
          email: data.email,
          class_id: null, // users table is for staff, no class_id
          coins: data.coins ?? 0,
        });
      }
    } catch (error) {
      console.error("Refresh user error:", error);
    }
  };

  const logout = signOut;

  return {
    user,
    loading,
    signInWithPin,
    signInWithEmail,
    signOut,
    logout,
    refreshUser,
  };
}
